/**
 * PipelineSection.tsx v8.0 — Layout 2 columnas, texto sin corte
 *
 * Vuelve al layout original de 2 cuadrados paralelos.
 * Fixes vs versiones anteriores:
 * - white-space: normal en pipe-txt-line (no nowrap) → texto sin corte
 * - font-size reducido en líneas de texto
 * - Datos más cortos para que quepan bien en el ancho disponible
 * - Cipher coherente: solo chars corruptos oscilan → toda la palabra a verde
 * - Cipher coords: visible desde el inicio, sección validación con fade
 */
import { type FC, useEffect, useRef, useState, useCallback } from 'react';
import { DOCUMENTS } from '../data';

export interface PipelineSectionProps {
  currentDocIndex: number;
  onDotClick:      (index: number) => void;
  totalDocs?:      number;
}

const DOC_BUTTONS = [
  { fmt: 'ODT',     label: 'Priego de Córdoba' },
  { fmt: 'XLSX',    label: 'Guadix'            },
  { fmt: 'DBF',     label: 'Colomera'          },
  { fmt: 'CSV',     label: 'Alfacar'           },
  { fmt: 'ODS',     label: 'Vélez-Mlg.'      },
  { fmt: 'GeoJSON', label: 'Almería'           },
] as const;

const PAUSE_MS  = 4000;
const FADE_MS   = 350;
const LINEIN_MS = 350;
const FRAME_MS  = 90;
const CYCLES    = 12;
const WORD_GAP  = 500;

// ─── Parser ───────────────────────────────────────────────────────────────────
type Segment = { type: 'text'; text: string }
             | { type: 'ok';   text: string; dirty: string };

function parseLine(html: string): Segment[] {
  const segs: Segment[] = [];
  const re = /<span class="hl-ok"(?:\s+data-dirty="([^"]*)")?>(.*?)<\/span>/g;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (m.index > last) segs.push({ type: 'text', text: html.slice(last, m.index) });
    segs.push({ type: 'ok', dirty: m[1] ?? m[2], text: m[2] });
    last = m.index + m[0].length;
  }
  if (last < html.length) segs.push({ type: 'text', text: html.slice(last) });
  return segs;
}

// ─── Cipher ───────────────────────────────────────────────────────────────────
function runCipherOnSpan(el: HTMLElement, clean: string, dirty: string): Promise<void> {
  return new Promise(resolve => {
    const ca = Array.from(clean), da = Array.from(dirty);
    const isDirty = ca.map((ch, i) => da[i] !== undefined && da[i] !== ch);
    const total   = isDirty.filter(Boolean).length;

    el.innerHTML = ca.map((ch, i) => {
      const init = isDirty[i] ? (da[i] ?? ch) : ch;
      return `<span class="cipher-ch" data-clean="${ch}" data-d="${isDirty[i] ? 1 : 0}">${init}</span>`;
    }).join('');

    const els = Array.from(el.querySelectorAll<HTMLElement>('.cipher-ch'));
    els.forEach(c => { c.style.color = c.dataset.d === '1' ? '#e8e8e8' : 'var(--fg-3)'; });

    if (total === 0) { els.forEach(c => { c.style.color = 'var(--lime)'; }); resolve(); return; }

    let done = 0;
    const noise = ['Ã','Â','©','±','ª','º','¿'];
    els.forEach((c, i) => {
      if (c.dataset.d !== '1') return;
      const target = c.dataset.clean ?? '';
      const init = da[i] ?? target;
      setTimeout(() => {
        let t = 0;
        const iv = setInterval(() => {
          if (++t >= CYCLES) {
            clearInterval(iv);
            c.textContent = target; c.style.color = 'var(--fg-3)';
            if (++done === total) { els.forEach(x => { x.style.color = 'var(--lime)'; }); resolve(); }
          } else {
            c.textContent = [init, ...noise][Math.floor(Math.random() * (noise.length + 1))];
            c.style.color = '#e8e8e8';
          }
        }, FRAME_MS);
      }, i * FRAME_MS * 1.5);
    });
  });
}

function runCipherOnCoord(el: HTMLElement, to: string, from: string): Promise<void> {
  return new Promise(resolve => {
    const ta = Array.from(to);
    const fa = Array.from(from);

    // Marcar qué posiciones difieren — si from y to tienen formatos muy distintos,
    // todos los chars animarán (cipher completo de ruido → correcto)
    const isDirty = ta.map((ch, i) => fa[i] === undefined || fa[i] !== ch);
    const total   = isDirty.filter(Boolean).length;

    // Construir spans — chars "sucios" arrancan con el char de from (o ruido)
    el.innerHTML = ta.map((ch, i) => {
      const init = (fa[i] !== undefined && fa[i] !== ch) ? fa[i] : ch;
      return `<span class="cipher-ch" data-clean="${ch}" data-d="${isDirty[i] ? 1 : 0}">${init}</span>`;
    }).join('');

    const els = Array.from(el.querySelectorAll<HTMLElement>('.cipher-ch'));
    els.forEach(c => { c.style.color = c.dataset.d === '1' ? '#e8e8e8' : 'var(--teal)'; });

    if (total === 0) { els.forEach(c => { c.style.color = 'var(--teal)'; }); resolve(); return; }

    let done = 0;
    const digits = '0123456789.,: '.split('');

    els.forEach((c, i) => {
      if (c.dataset.d !== '1') return;
      const target    = c.dataset.clean ?? '';
      const initChar  = fa[i] ?? digits[Math.floor(Math.random() * digits.length)];

      setTimeout(() => {
        let t = 0;
        const iv = setInterval(() => {
          if (++t >= CYCLES) {
            clearInterval(iv);
            c.textContent = target;
            c.style.color = 'var(--fg-3)';
            if (++done === total) {
              // Toda la coord vira a teal de golpe
              els.forEach(x => { x.style.color = 'var(--teal)'; });
              resolve();
            }
          } else {
            c.textContent = t % 2 === 0
              ? initChar
              : digits[Math.floor(Math.random() * digits.length)];
            c.style.color = '#e8e8e8';
          }
        }, FRAME_MS);
      }, i * FRAME_MS * 1.2);
    });
  });
}

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconDoc = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);
const IconPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);
const ArrowRight = ({ color }: { color: string }) => (
  <svg viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
    <path d="M3 12H21" strokeLinecap="round" strokeWidth="2" stroke={color} />
    <path d="M13.5 4.5L21 12L13.5 19.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" stroke={color} />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────
export const PipelineSection: FC<PipelineSectionProps> = ({
  currentDocIndex, onDotClick, totalDocs = 6,
}) => {
  const doc         = DOCUMENTS[currentDocIndex];
  const fmt         = DOC_BUTTONS[currentDocIndex]?.fmt ?? 'ODT';
  const coordRows   = doc.step3;
  const validRows   = doc.step4;
  const parsedLines = doc.step2.map(parseLine);

  const [isFading,       setIsFading]       = useState(false);
  const [arrowsVisible,  setArrowsVisible]  = useState(false);
  const [coordsStarted,  setCoordsStarted]  = useState(false);
  const [validVisible,   setValidVisible]   = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const activeRef  = useRef(false);
  const timersRef  = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  const schedule = (fn: () => void, ms: number) => { const t = setTimeout(fn, ms); timersRef.current.push(t); };
  const advanceWithFade = useCallback((next: number) => {
    setIsFading(true); setTimeout(() => onDotClick(next), FADE_MS);
  }, [onDotClick]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    activeRef.current = true;
    clearTimers();
    setIsFading(false); setArrowsVisible(false); setCoordsStarted(false); setValidVisible(false);

    const start = () => {
      section.classList.add('pipe--visible');
      schedule(() => {
        if (!activeRef.current) return;
        setArrowsVisible(true);

        const okSpans = Array.from(section.querySelectorAll<HTMLElement>('.pipe-ok-span'));

        const np = okSpans.map((sp, i) => new Promise<void>(res => {
          setTimeout(() => {
            if (!activeRef.current) { res(); return; }
            runCipherOnSpan(sp, sp.dataset.original ?? '', sp.dataset.dirty ?? '').then(res);
          }, i * WORD_GAP);
        }));

        const coordSpans = Array.from(section.querySelectorAll<HTMLElement>('.pipe-coord-span'));

        // Al terminar nombres → activar flecha coords + cipher coords
        Promise.all(np).then(() => {
          if (!activeRef.current) return;
          setCoordsStarted(true);

          // Cipher coords en secuencia escalonada
          const coordPromises = coordSpans.map((sp, i) =>
            new Promise<void>(res => {
              setTimeout(() => {
                if (!activeRef.current) { res(); return; }
                runCipherOnCoord(sp, sp.dataset.to ?? '', sp.dataset.from ?? '').then(res);
              }, i * WORD_GAP);
            })
          );

          // Al terminar coords → mostrar validación (fase 3)
          Promise.all(coordPromises).then(() => {
            if (!activeRef.current) return;
            setValidVisible(true);

            schedule(() => {
              if (!activeRef.current) return;
              advanceWithFade((currentDocIndex + 1) % totalDocs);
            }, PAUSE_MS);
          });
        });
      }, LINEIN_MS);
    };

    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) { start(); }
    else {
      const io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { io.disconnect(); start(); }
      }, { threshold: 0.15 });
      io.observe(section);
      return () => { activeRef.current = false; clearTimers(); io.disconnect(); section.classList.remove('pipe--visible'); };
    }
    return () => { activeRef.current = false; clearTimers(); section.classList.remove('pipe--visible'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDocIndex]);


  // ── JSX: 2 columnas paralelas ────────────────────────────────────────────
  return (
    <section ref={sectionRef} className="pipe" id="como-funciona">
      <div className="pipe-inner">
        <div className="pipe-hd">
          <div className="pipe-eyebrow">Cómo funciona</div>
          <h2 className="pipe-title">Así transforma y corrige PTEL Tool los datos de los informes</h2>
          <p className="pipe-sub">De texto corrupto a coordenadas validadas en QGIS</p>
        </div>

        <div className={`pipe-grid${isFading ? ' pipe-grid--fading' : ''}`}>

          {/* ══ CARD 1: Nombres y direcciones ══ */}
          <div className="pipe-dual">
            <div className="pipe-dual-hd">
              <div className="pipe-dual-hd-left">
                <div className="pipe-dual-icon pipe-dual-icon--txt"><IconDoc /></div>
                <span className="pipe-dual-title">Nombres y direcciones</span>
              </div>
              <span className="pipe-dual-doc">{doc.name}</span>
            </div>
            <div className="pipe-dual-body">
              <div className="pipe-dual-half pipe-dual-half--left">
                <div className="pipe-dual-half-label pipe-dual-half-label--before">Entrada corrupta</div>
                {doc.step1.map((line, i) => (
                  <div key={i} className="pipe-txt-line" dangerouslySetInnerHTML={{ __html: line }} />
                ))}
                <div className="pipe-dual-extra"><strong>116 patrones</strong> UTF-8</div>
              </div>
              <div className={`pipe-dual-arrow${arrowsVisible ? ' pipe-dual-arrow--visible' : ''}`}>
                <ArrowRight color="var(--teal)" />
              </div>
              <div className="pipe-dual-half">
                <div className="pipe-dual-half-label pipe-dual-half-label--after">Corregido ✓</div>
                {parsedLines.map((segs, li) => (
                  <div key={li} className="pipe-txt-line pipe-txt-line--right">
                    {segs.map((seg, si) =>
                      seg.type === 'text'
                        ? <span key={si}>{seg.text}</span>
                        : <span key={si} className="pipe-ok-span"
                            data-original={seg.text} data-dirty={seg.dirty}
                          >{seg.text}</span>
                    )}
                  </div>
                ))}
                <div className="pipe-dual-extra">
                  <strong>{doc.step1.length} campos</strong> corregidos
                </div>
              </div>
            </div>
            <div className="pipe-dual-footer">
              <span className="pipe-dual-footer-ok">{doc.step1.length} campos corregidos</span>
              <span className="pipe-dual-footer-sep">·</span>
              <span>116 patrones UTF-8</span>
              <span className="pipe-dual-footer-sep">·</span>
              <span className="pipe-dual-footer-fmt">{fmt}</span>
            </div>
          </div>

          {/* ══ CARD 2: Coordenadas ══ */}
          <div className="pipe-dual">
            <div className="pipe-dual-hd">
              <div className="pipe-dual-hd-left">
                <div className="pipe-dual-icon pipe-dual-icon--geo"><IconPin /></div>
                <span className="pipe-dual-title">Coordenadas</span>
              </div>
              <span className="pipe-dual-doc">{doc.name}</span>
            </div>
            <div className="pipe-dual-body">
              {/* IZQ: coords originales + metadatos del archivo */}
              <div className="pipe-dual-half pipe-dual-half--left">
                <div className="pipe-dual-half-label pipe-dual-half-label--norm">Formato original</div>
                {coordRows.map((item, i) => (
                  <div key={i} className="pipe-conv-row">
                    <span className="pipe-conv-from">{item.from}</span>
                  </div>
                ))}
                <div className="pipe-meta-block">
                  <div className="pipe-meta-title">Metadatos fuente</div>
                  {doc.step3meta.map((item, i) => (
                    <div key={i} className="pipe-meta-row">
                      <span className="pipe-meta-lbl">{item.label}</span>
                      <span className="pipe-meta-val">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`pipe-dual-arrow${coordsStarted ? ' pipe-dual-arrow--visible' : ''}`}>
                <ArrowRight color="var(--teal)" />
              </div>
              <div className="pipe-dual-half">
                <div className="pipe-dual-half-label pipe-dual-half-label--valid">UTM30 · EPSG:25830</div>
                {coordRows.map((item, i) => (
                  <div key={i} className="pipe-conv-row">
                    <span className="pipe-coord-span"
                      data-to={item.to} data-from={item.from}>{item.to}</span>
                  </div>
                ))}
                <div className="pipe-valid-block pipe-valid-block--visible">
                  <div className="pipe-coord-divider" />
                  {validRows.map((item, i) => (
                    <div key={i} className="pipe-res-row">
                      <span className="pipe-res-lbl">{item.label}</span>
                      <span className={`${i === 0 ? 'pipe-res-val-hi' : 'pipe-res-val-teal'} pipe-res-val${validVisible ? ' pipe-res-val--visible' : ''}`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="pipe-dual-footer">
              <span className="pipe-dual-footer-ok">{doc.step4[0]?.value}</span>
              <span className="pipe-dual-footer-sep">·</span>
              <span>EPSG:25830</span>
              <span className="pipe-dual-footer-sep">·</span>
              <span className="pipe-dual-footer-fmt">{fmt}</span>
            </div>
          </div>

        </div>

        <div className="pipe-doc-sel">
          <div className="pipe-doc-btns">
            {DOC_BUTTONS.map((btn, i) => (
              <button key={i} type="button"
                className={`pipe-doc-btn${i === currentDocIndex ? ' pipe-doc-btn--active' : ''}`}
                onClick={() => onDotClick(i)}>
                <span className="pipe-doc-fmt">{btn.fmt}</span>
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PipelineSection;
